import { Dependency, ErrorIndicator, Warning } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useTranslation } from 'react-i18next';
import { isItemDependencyAtRisk, isItemDependencyBlocked } from '@/lib/api/types/dashboard/health';
import { DependencyType } from '@/lib/api/types/items';
import { useItemPanelNavigation } from '@/lib/hooks/item-panel/useItemPanelParams';
import { TabValue } from '@/lib/types/itemPanel';
import { useVisibleDependencies } from '../context/VisibleDependenciesContext';
import { useVisibleItemHealth } from '../context/VisibleHealthContext';
import type { ItemsTableRow } from '../types';

export const DependencyStatusCell = ({ row }: { row: ItemsTableRow }) => {
  const { t } = useTranslation();
  const { openItemPanel } = useItemPanelNavigation();
  const health = useVisibleItemHealth(row.id);
  const liveDependencies = useVisibleDependencies(row.id);
  const dependencies = liveDependencies ?? row.dependencies;

  const hasDeps = (dependencies?.length ?? 0) > 0;
  const hasPredecessors =
    dependencies?.some((d) => d.dependencyType === DependencyType.Predecessor) ?? false;

  if (!hasDeps) return null;

  const dependencyStatus =
    hasPredecessors && !!health
      ? isItemDependencyBlocked(health)
        ? 'blocked'
        : isItemDependencyAtRisk(health)
          ? 'atRisk'
          : null
      : null;

  const statusTooltip =
    dependencyStatus === 'blocked'
      ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.blockedTooltip)
      : dependencyStatus === 'atRisk'
        ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.atRiskTooltip)
        : null;

  return (
    <TooltipProvider>
      <button
        type="button"
        aria-label={
          statusTooltip
            ? `${t(($) => $.loopVisualization.timelineTooltip.openDependencies)} — ${statusTooltip}`
            : t(($) => $.loopVisualization.timelineTooltip.openDependencies)
        }
        className="flex w-full items-center cursor-pointer"
        onClick={() => openItemPanel({ itemId: row.id, panelTab: TabValue.DEPENDENCIES })}
      >
        <Dependency className="size-4 text-icon-neutral-secondary" />
        {dependencyStatus && (
          <Tooltip>
            <TooltipTrigger asChild>
              {dependencyStatus === 'blocked' ? (
                <ErrorIndicator className="ml-auto size-4 text-icon-error" />
              ) : (
                <Warning className="ml-auto size-4 text-icon-warning" />
              )}
            </TooltipTrigger>
            <TooltipContent>{statusTooltip}</TooltipContent>
          </Tooltip>
        )}
      </button>
    </TooltipProvider>
  );
};
